import React from 'react';

export default function AttendancePatterns({ patterns }) {
  console.log('AttendancePatterns received patterns:', patterns);
  
  if (!patterns) {
    console.log('No patterns provided');
    return null;
  }

  const getPatternIcon = (type) => {
    switch (type) {
      case 'trend':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        );
      case 'day':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      case 'behavior':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        );
      case 'improvement':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const getPatternColor = (type, severity) => {
    if (type === 'improvement') return 'text-green-600 bg-green-50 border-green-200';
    
    switch (severity) {
      case 'high':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      default:
        return 'text-slate-600 bg-slate-50 border-slate-200';
    }
  };

  const formatPercentage = (value) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Attendance Trends - Always show */}
      <div className="bg-slate-50 rounded-lg p-4">
        <div className="flex items-center mb-3">
          <div className="flex-shrink-0">
            <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
              {getPatternIcon('trend')}
            </div>
          </div>
          <div className="ml-3">
            <h4 className="font-semibold text-slate-800">Trends</h4>
            <p className="text-xs text-slate-600">Pattern analysis</p>
          </div>
        </div>

        <div className="space-y-2">
          {patterns.trends && patterns.trends.length > 0 ? (
            patterns.trends.slice(0, 2).map((trend, index) => (
              <div
                key={index}
                className={`p-3 rounded-md border ${getPatternColor('trend', trend.severity)}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center">
                      {getPatternIcon(trend.type || 'trend')}
                      <h5 className="ml-2 text-sm font-medium">{trend.description}</h5>
                    </div>
                    {trend.details && (
                      <p className="mt-1 text-xs opacity-90">{trend.details}</p>
                    )}
                  </div>
                  {trend.value && (
                    <div className="ml-3 text-right">
                      <div className="text-sm font-semibold">{formatPercentage(trend.value)}</div>
                      <div className="text-xs opacity-75">Change</div>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="p-3 text-center text-slate-500">
              <svg className="w-8 h-8 mx-auto mb-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              <p className="text-xs">No trends detected</p>
              <p className="text-xs mt-1">Need more data</p>
            </div>
          )}
        </div>
      </div>

      {/* Behavioral Insights - Always show */}
      <div className="bg-slate-50 rounded-lg p-4">
        <div className="flex items-center mb-3">
          <div className="flex-shrink-0">
            <div className="p-2 bg-purple-100 rounded-lg text-purple-600">
              {getPatternIcon('behavior')}
            </div>
          </div>
          <div className="ml-3">
            <h4 className="font-semibold text-slate-800">Insights</h4>
            <p className="text-xs text-slate-600">Notable patterns</p>
          </div>
        </div>

        <div className="space-y-2">
          {patterns.insights && patterns.insights.length > 0 ? (
            patterns.insights.slice(0, 2).map((insight, index) => (
              <div
                key={index}
                className={`p-3 rounded-md border ${getPatternColor(insight.type, insight.severity)}`}
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    {getPatternIcon(insight.type || 'behavior')}
                  </div>
                  <div className="ml-2 flex-1">
                    <h5 className="text-sm font-medium">{insight.title}</h5>
                    <p className="mt-1 text-xs">{insight.description}</p>
                    {insight.recommendation && (
                      <div className="mt-2 p-2 bg-white bg-opacity-50 rounded border">
                        <p className="text-xs font-medium">💡 Tip:</p>
                        <p className="text-xs mt-1">{insight.recommendation}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-3 text-center text-slate-500">
              <svg className="w-8 h-8 mx-auto mb-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <p className="text-xs">No insights found</p>
              <p className="text-xs mt-1">Patterns emerging</p>
            </div>
          )}
        </div>
      </div>

      {/* Improvement Suggestions - Always show */}
      <div className="bg-slate-50 rounded-lg p-4">
        <div className="flex items-center mb-3">
          <div className="flex-shrink-0">
            <div className="p-2 bg-emerald-100 rounded-lg text-emerald-600">
              {getPatternIcon('improvement')}
            </div>
          </div>
          <div className="ml-3">
            <h4 className="font-semibold text-slate-800">Improvements</h4>
            <p className="text-xs text-slate-600">Action items</p>
          </div>
        </div>

        <div className="space-y-2">
          {patterns.improvements && patterns.improvements.length > 0 ? (
            patterns.improvements.slice(0, 2).map((improvement, index) => (
              <div
                key={index}
                className="p-3 rounded-md border border-emerald-200 bg-emerald-50"
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0 text-emerald-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3l14 9-14 9V3z" />
                    </svg>
                  </div>
                  <div className="ml-2 flex-1">
                    <h5 className="text-sm font-medium text-emerald-800">{improvement.title}</h5>
                    <p className="mt-1 text-xs text-emerald-700">{improvement.description}</p>
                    {improvement.priority && (
                      <div className="mt-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          improvement.priority === 'high' 
                            ? 'bg-red-100 text-red-800' 
                            : improvement.priority === 'medium'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {improvement.priority.toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-3 text-center text-slate-500">
              <svg className="w-8 h-8 mx-auto mb-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-xs">All good!</p>
              <p className="text-xs mt-1">No action needed</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
